/**
 * Authentication Service for Outlook Add-in
 * Handles login, token refresh, and secure token storage
 */

import config from '../../config';
import { getPostHogClient } from '../posthog';
const API_BASE_URL = config.AUTH_BASE_URL;

class AuthService {
  constructor() {
    this.accessToken = null;
    this.refreshToken = null;
    this.expiresAt = null;
    this.user = null;
    this.refreshTimer = null;
    this.rememberMe = false;
    
    // Load tokens from storage on initialization
    this.loadTokens();
  }

  /**
   * Load tokens from persistent (localStorage) or session storage
   * Priority: localStorage (persistent, survives Outlook close) > sessionStorage (temporary)
   */
  async loadTokens() {
    try {
      let stored = null;
      let isPersistent = false;
      
      // 1. First, try to load from localStorage (persistent - survives Outlook close)
      try {
        stored = localStorage.getItem('donexus_auth');
        if (stored) {
          isPersistent = true;
          this.rememberMe = true;
          console.log('✅ Loaded auth from localStorage (persistent)');
        }
      } catch (error) {
        console.warn('localStorage not available:', error);
      }
      
      // 2. Fall back to sessionStorage if no persistent storage found
      if (!stored) {
        try {
          stored = sessionStorage.getItem('donexus_auth');
          if (stored) {
            isPersistent = false;
            console.log('📋 Loaded auth from sessionStorage (temporary)');
          }
        } catch (error) {
          console.warn('sessionStorage not available:', error);
        }
      }
      
      if (stored) {
        const authData = JSON.parse(stored);
        this.accessToken = authData.accessToken;
        this.refreshToken = authData.refreshToken;
        this.expiresAt = authData.expiresAt;
        this.user = authData.user;
        this.rememberMe = authData.rememberMe || isPersistent;
        
        // Check if token is still valid
        if (this.isTokenValid()) {
          console.log('✅ Token is valid, scheduling refresh');
          this.scheduleTokenRefresh();
          this.identifyUser(this.user);
        } else {
          // Token expired - try silent refresh if we have a refresh token
          console.log('⚠️ Token expired, attempting silent refresh...');
          if (this.refreshToken) {
            try {
              await this.refreshAccessToken();
              console.log('✅ Silent token refresh successful');
            } catch (error) {
              console.error('❌ Silent token refresh failed:', error);
              await this.clearTokens();
            }
          } else {
            console.log('❌ No refresh token available, clearing tokens');
            await this.clearTokens();
          }
        }
      }
    } catch (error) {
      console.error('Error loading tokens:', error);
      await this.clearTokens();
    }
  }

  /**
   * Save tokens to persistent (localStorage) or session storage
   * Uses localStorage for "Remember Me" (persists after Outlook closes)
   * Uses sessionStorage when not remembered (clears on close)
   */
  async saveTokens() {
    try {
      const authData = {
        accessToken: this.accessToken,
        refreshToken: this.refreshToken,
        expiresAt: this.expiresAt,
        user: this.user,
        rememberMe: this.rememberMe
      };
      
      if (this.rememberMe) {
        // "Remember Me" enabled - use localStorage (persists after Outlook closes)
        try {
          localStorage.setItem('donexus_auth', JSON.stringify(authData));
          console.log('✅ Saved auth to localStorage (persistent)');
          
          // Clear sessionStorage to prevent confusion/zombie tokens
          try {
            sessionStorage.removeItem('donexus_auth');
          } catch (e) {
            // Ignore sessionStorage errors
          }
          return;
        } catch (error) {
          console.error('Error saving to localStorage:', error);
          // Fall through to sessionStorage as last resort
        }
      }
      
      // "Remember Me" disabled OR localStorage failed - use sessionStorage (clears on close)
      try {
        sessionStorage.setItem('donexus_auth', JSON.stringify(authData));
        console.log('📋 Saved auth to sessionStorage (temporary)');
        
        // Clear localStorage when user opts out of "Remember Me"
        // This prevents loading old persistent data on next session
        try {
          localStorage.removeItem('donexus_auth');
        } catch (e) {
          // Ignore localStorage errors
        }
      } catch (error) {
        console.error('Error saving to sessionStorage:', error);
      }
    } catch (error) {
      console.error('Error saving tokens:', error);
    }
  }

  /**
   * Clear all tokens and user data from all storage locations
   */
  async clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    this.expiresAt = null;
    this.user = null;
    this.rememberMe = false;

    this.resetPostHogIdentity();
    
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    
    // Clear from all storage locations
    try {
      sessionStorage.removeItem('donexus_auth');
    } catch (error) {
      console.warn('Could not clear sessionStorage:', error);
    }
    
    try {
      localStorage.removeItem('donexus_auth');
    } catch (error) {
      console.warn('Could not clear localStorage:', error);
    }
    
    console.log('🗑️ Cleared auth from all storage locations');
  }

  /**
   * Check if current access token is valid
   */
  isTokenValid() {
    if (!this.accessToken || !this.expiresAt) {
      return false;
    }
    
    // Add 5 minute buffer before expiry
    const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
    return Date.now() < (this.expiresAt * 1000 - bufferTime);
  }

  /**
   * Get current access token
   */
  getAccessToken() {
    console.log('🔑 authService.getAccessToken called:', {
      hasToken: !!this.accessToken,
      tokenLength: this.accessToken?.length,
      tokenPreview: this.accessToken ? `${this.accessToken.substring(0, 30)}...` : 'null',
      isValid: this.isTokenValid(),
      expiresAt: this.expiresAt ? new Date(this.expiresAt).toISOString() : 'null'
    });
    return this.accessToken;
  }

  /**
   * Get current user
   */
  getCurrentUser() {
    return this.user;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return this.isTokenValid() && this.user !== null;
  }

  /**
   * Login with email and password
   */
  async login(email, password, rememberMe = false) {
    try {
      const response = await fetch(`${API_BASE_URL}/login-addin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          context: 'outlook-addin'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      if (data.success) {
        this.accessToken = data.accessToken;
        this.refreshToken = data.refreshToken;
        this.expiresAt = data.expiresAt;
        this.user = data.user || null;
        this.rememberMe = rememberMe;
        
        await this.saveTokens();
        this.scheduleTokenRefresh();
        this.identifyUser(this.user);
        
        return {
          success: true,
          user: this.user,
          message: data.message
        };
      } else {
        throw new Error(data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      await this.clearTokens();
      throw error;
    }
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshAccessToken() {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/refresh-addin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: this.refreshToken,
          context: 'outlook-addin'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Token refresh failed');
      }

      if (data.success) {
        this.accessToken = data.accessToken;
        this.refreshToken = data.refreshToken;
        this.expiresAt = data.expiresAt;
        this.user = data.user || this.user;
        
        await this.saveTokens();
        this.scheduleTokenRefresh();
        this.identifyUser(this.user);
        
        return {
          success: true,
          user: this.user,
          message: data.message
        };
      } else {
        throw new Error(data.message || 'Token refresh failed');
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      await this.clearTokens();
      throw error;
    }
  }

  /**
   * Schedule automatic token refresh
   */
  scheduleTokenRefresh() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    if (!this.expiresAt) return;

    // Calculate time to refresh (5 minutes before expiry)
    const refreshTime = (this.expiresAt * 1000) - (5 * 60 * 1000);
    const timeUntilRefresh = refreshTime - Date.now();

    if (timeUntilRefresh > 0) {
      this.refreshTimer = setTimeout(async () => {
        try {
          await this.refreshAccessToken();
        } catch (error) {
          console.error('Automatic token refresh failed:', error);
          await this.clearTokens();
          // Trigger logout event
          window.dispatchEvent(new CustomEvent('auth:logout'));
        }
      }, timeUntilRefresh);
    }
  }

  /**
   * Identify the current user in PostHog
   */
  identifyUser(user) {
    if (!user || !user.id) {
      return;
    }

    try {
      const client = getPostHogClient();
      if (typeof client?.identify !== "function") {
        return;
      }

      const organisationId =
        user.organisation_id ||
        user?.organisationId ||
        user?.organisation ||
        user?.user_metadata?.organisation_id;

      const role =
        user?.role ||
        user?.user_metadata?.role ||
        user?.user_metadata?.roles;

      client.identify(String(user.id), {
        email: user.email,
        name: user.name,
        organisation_id: organisationId,
        role,
      });
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[PostHog] Failed to identify user", error);
      }
    }
  }

  /**
   * Reset PostHog identity on logout or token clear
   */
  resetPostHogIdentity() {
    try {
      const client = getPostHogClient();
      if (typeof client?.reset === "function") {
        client.reset();
      }
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[PostHog] Failed to reset identity", error);
      }
    }
  }

  /**
   * Logout user
   */
  async logout() {
    await this.clearTokens();
    window.dispatchEvent(new CustomEvent('auth:logout'));
  }

  /**
   * Make authenticated API request
   */
  async makeAuthenticatedRequest(url, options = {}) {
    if (!this.isTokenValid()) {
      if (this.refreshToken) {
        try {
          await this.refreshAccessToken();
        } catch (error) {
          throw new Error('Authentication required');
        }
      } else {
        throw new Error('Authentication required');
      }
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.accessToken}`,
      ...options.headers
    };

    return fetch(url, {
      ...options,
      headers
    });
  }
}

// Create singleton instance
const authService = new AuthService();

export default authService;
